import {AttrsSchema, Component, ConfigOptions} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';

import {template} from './template';
import './index.styl';

// Keep this enum in sync with the corresponding values in analytics/user/security/twofactor.py.
// Note that because the two sets of values are in two different binaries, you may not be able
// to make changes in a signle PR! Instead, you may have to make a series of backwards-compatible
// changes over several PRs.
export enum TwoFactorOption {
  NOT_SELECTED = `not selected`,
  SMS = `sms`,
  TOTP = `totp`,
}

export enum FocusedTwoFactorOption {
  NO_FOCUS = `no focus`,
  SMS = `sms`,
  TOTP = `totp`,
}

interface TwoFactorSelectionMenuState {
  previousOption: TwoFactorOption;
  focusedOption: FocusedTwoFactorOption;
  // excluding NotSelected as we highlight TOTP as default, and there is always one highlighted item
  highlightedTwoFactorOption: Exclude<TwoFactorOption, TwoFactorOption.NOT_SELECTED>;
}

export interface TwoFactorSelectionMenuAttrs {
  'previous-option': `string`;
}

export class TwoFactorSelectionMenu extends Component<TwoFactorSelectionMenuState, TwoFactorSelectionMenuAttrs> {
  static get attrsSchema(): AttrsSchema<TwoFactorSelectionMenuAttrs> {
    return {
      ...super.attrsSchema,
      'previous-option': `string`,
    };
  }

  get config(): ConfigOptions<TwoFactorSelectionMenuState> {
    return {
      template,
      defaultState: {
        previousOption: TwoFactorOption.NOT_SELECTED,
        focusedOption: FocusedTwoFactorOption.NO_FOCUS,
        highlightedTwoFactorOption: TwoFactorOption.TOTP,
      },
      helpers: {
        FocusedTwoFactorOption,
        TwoFactorOption,
        callIfKeyboardInteraction: (ev: KeyboardEvent, callback: () => void): void => {
          if (ev.key === ` ` || ev.key === `Enter`) {
            callback();
          }
        },
        updateFocusedOption: (option: FocusedTwoFactorOption): void => {
          this.update({focusedOption: option});
        },
        updateTwoFactorSelection: (newSelection: Exclude<TwoFactorOption, TwoFactorOption.NOT_SELECTED>): void => {
          this.update({highlightedTwoFactorOption: newSelection});
        },
      },
    };
  }

  private castToTwoFactorOption(input: string): TwoFactorOption | null {
    return (
      Object.values(TwoFactorOption).find((enumValue) => {
        return enumValue === input;
      }) || null
    );
  }

  connectedCallback(): void {
    super.connectedCallback();
    if (this.attr(`previous-option`)) {
      this.update({
        previousOption: this.castToTwoFactorOption(this.attr(`previous-option`)) || TwoFactorOption.NOT_SELECTED,
      });
    }
  }
}

export const TwoFactorSelectionMenuEl = defineAndMakeJsxFactory(`mp-two-factor-selection-menu`, TwoFactorSelectionMenu);
export const TwoFactorSelectionMenuReact = panel2react(`mp-two-factor-selection-menu`, TwoFactorSelectionMenu);
